import TableC from "@/CommonElements/TableC";
import BtnAsLink from "@/Component/BtnAsLink";
import { useDeleteHouseExaminationItemMutation } from "@/services/basicData";
import { useHouseExaminationItemsQuery } from "@/services/houseInspections";
import { useConfirm } from "@/utils/hooks/useConfrim";
import { useDebouncedSearch } from "@/utils/hooks/useDebouncedSearch";
import {
  AddSquareIcon,
  DeleteIcon,
  Edit02Icon,
} from "@hugeicons/core-free-icons/index";
import { HugeiconsIcon } from "@hugeicons/react";
import { Tag } from "antd";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

export const HouseExaminationItemsList = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const { searchValue, debouncedValue, setSearchValue } = useDebouncedSearch(
    "",
    500
  );
  const { t } = useTranslation();
  const { confirm, ModalComponent } = useConfirm();

  // ==============
  //   Side Effects
  // ==============
  const {
    mutate: deleteHouseExaminationItem,
    isPending: isDeleteHouseExaminationItemPending,
  } = useDeleteHouseExaminationItemMutation();
  const { data: houseExaminationItemsRes, isLoading } =
    useHouseExaminationItemsQuery({
      params: {
        page: currentPage,
        per_page: 10,
        query: debouncedValue,
      },
    });
  const houseExaminationItems = houseExaminationItemsRes?.data?.data ?? [];
  const meta = houseExaminationItemsRes?.data?.meta ?? {};

  const columns = [
    {
      name: t("NAME_AR"),
      key: "name_ar",
    },
    {
      name: t("NAME_EN"),
      key: "name_en",
    },
    {
      name: t("STATUS"),
      key: "is_active",
      render: (value, rowData) => (
        <Tag color={value ? "success" : "error"}>
          {value ? t("ACTIVE") : t("INACTIVE")}
        </Tag>
      ),
    },
    {
      name: t("ACTIONS"),
      key: "actions",
      filterable: false,
    },
  ];

  // =========
  // functions
  // =========
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleDeleteClick = (id) => {
    confirm({
      title: t("CONFIRM_DELETE_HOUSE_EXAMINATION_ITEM_TITLE"),
      content: t("CONFIRM_DELETE_HOUSE_EXAMINATION_ITEM_MESSAGE"),
      okText: t("CONFIRM"),
      cancelText: t("CANCEL"),
      onOk: ({ closeModal }) => {
        deleteHouseExaminationItem(
          { id },
          {
            onSuccess: () => {
              closeModal();
            },
          }
        );

        return false;
      },
      onCancel: () => {},
    });
  };

  // Define actions function
  const getActions = (rowData) => {
    return [
      {
        key: "edit",
        label: (
          <Link
            to={`/app/makkah/basic-data/house_examination_items/${rowData.id}/edit`}
            className="d-flex align-items-center gap-2"
          >
            <HugeiconsIcon size={16} icon={Edit02Icon} />
            {t("EDIT")}
          </Link>
        ),
      },
      {
        type: "divider",
      },
      {
        key: "delete",
        label: (
          <div className="d-flex align-items-center gap-2">
            <HugeiconsIcon size={16} icon={DeleteIcon} />
            {t("DELETE")}
          </div>
        ),
        onClick: () => handleDeleteClick(rowData.id),
        danger: true,
      },
    ];
  };

  const handleSearchChange = (e) => {
    setSearchValue(e.target.value);
    setCurrentPage(1); // Reset to first page on search
  };

  const createButton = (
    <BtnAsLink
      className="btn-primary"
      icon={<HugeiconsIcon icon={AddSquareIcon} />}
      link="/app/makkah/basic-data/house_examination_items/create"
    >
      {t("CREATE_HOUSE_EXAMINATION_ITEM")}
    </BtnAsLink>
  );

  return (
    <div>
      <TableC
        data={houseExaminationItems}
        // {...restClientSearch}
        searchValue={searchValue}
        onSearchChange={handleSearchChange}
        columns={columns}
        loading={isLoading}
        actions={getActions}
        renderActions={createButton}
        // emptyState={EmptyState}
        // minVisibleColumns={2}
        skeletonCount={10}
        pagination={{
          current: currentPage,
          total: meta?.total ?? 0,
          pageSize: meta?.per_page ?? 10,
          onChange: handlePageChange,
        }}
      />
      <ModalComponent />
    </div>
  );
};
